import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE, ASC } from "const"
import {
    MarkovTransition,
    MarkovTransitionCreate,
} from "server/models"
import { StateType } from "client/utils"
import {
    StoreType,
    MarkovTransitionValidation,

    MarkovExpressionConvertToView,
    MarkovExpressionParseElements,
    MarkovExpressionCalc,
    SortMarkovStates,
    SaveMarkovTransition,

    setMarkovTransitionEdit,
    setMarkovTransitionReloadList,
    setMarkovTransitionReloadEdit,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    SelectDropdown,
    SelectDropdownItem,
    SelectDropdownSearch,
    Input,
    Feedback,
    Button,
    SpinnerIcon,
    Empty,
} from "client/components"
import ExpressionEditor from "client/pages/markov/expressionEditor"

interface EditTransitionProps {
    transition: MarkovTransition
}

let isMounted: boolean

const Form = ({
    transition,
}: EditTransitionProps) => {
    const model = useSelector((state: StoreType) => state.markovModel.modelView)
    const stateList = useSelector((state: StoreType) => state.markovState.stateList)
    const variableList = useSelector((state: StoreType) => state.markovVariable.variableList)
    const variableListByName = useSelector((state: StoreType) => state.markovVariable.variableListByName)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [stateFrom, setStateFrom] = useState(transition.stateFrom)
    const [stateTo, setStateTo] = useState(transition.stateTo)
    const [expression, setExpression] = useState(transition.expression)
    const [order, setOrder] = useState(transition.order)

    const [stateFromIds, setStateFromIds] = useState<number[]>([])
    const [stateToIds, setStateToIds] = useState<number[]>([])

    const [searchFrom, setSearchFrom] = useState<string>("")
    const [searchTo, setSearchTo] = useState<string>("")

    const [validation, setValidation] = useState<MarkovTransitionValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        setExpression(MarkovExpressionConvertToView(transition.expression, variableList))

        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (isMounted && modals.length) dispatch(closeModal())
    }

    useEffect(() => {
        const sortedIds = SortMarkovStates(Object.values(stateList), "order", ASC, { search: searchFrom })
        setStateFromIds(sortedIds)
    }, [stateList, searchFrom])

    useEffect(() => {
        const sortedIds = SortMarkovStates(Object.values(stateList), "order", ASC, { search: searchTo })
        setStateToIds(sortedIds)
    }, [stateList, searchTo])

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        // replace variable names with var_IDs
        let expressionWithVars = ""
        if (expression.trim().length) {
            const parsedExp = MarkovExpressionParseElements(expression.trim())
            parsedExp.forEach((parsed) => {
                if (variableListByName[parsed]) expressionWithVars += `var_${variableListByName[parsed].id}`
                else expressionWithVars += parsed
            })

            // validate and calculate result expression
            const calcResult = MarkovExpressionCalc(null, expressionWithVars, variableList, lang)
            if (calcResult.error) {
                setValidation({ expression: calcResult.error })
                setSaveState(IDLE)
                setApplyState(IDLE)
                return
            }
        }

        const newTransitionData: MarkovTransitionCreate = {
            modelId: transition.modelId || model.id,
            stateFrom,
            stateTo,
            expression: expressionWithVars,
            order,
        }

        SaveMarkovTransition(
            transition.id,
            newTransitionData,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setMarkovTransitionEdit({
                id: res.newTransitionId || transition.id,
                ...newTransitionData,
            }))

            dispatch(setMarkovTransitionReloadList(false))
            setTimeout(() => {
                dispatch(setMarkovTransitionReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setMarkovTransitionReloadEdit(false))
                setTimeout(() => {
                    dispatch(setMarkovTransitionReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.stateFrom}</Label>
                        <SelectDropdown title={stateList[stateFrom] ? stateList[stateFrom].name : <Empty />}>
                            <SelectDropdownSearch
                                value={searchFrom}
                                onChange={(e) => setSearchFrom(e.target.value)}
                                onClearSearch={() => setSearchFrom("")}
                            />
                            {
                                stateFromIds.map((id) => (
                                    <SelectDropdownItem key={id} onClick={() => setStateFrom(id)}>
                                        {stateList[id] && stateList[id].name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.stateFrom}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.stateTo}</Label>
                        <SelectDropdown title={stateList[stateTo] ? stateList[stateTo].name : <Empty />}>
                            <SelectDropdownSearch
                                value={searchTo}
                                onChange={(e) => setSearchTo(e.target.value)}
                                onClearSearch={() => setSearchTo("")}
                            />
                            {
                                stateToIds.map((id) => (
                                    <SelectDropdownItem key={id} onClick={() => setStateTo(id)}>
                                        {stateList[id] && stateList[id].name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.stateTo}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.expression}</Label>
                        <ExpressionEditor
                            value={expression}
                            onChange={(val) => setExpression(val)}
                        />
                        <Feedback>{validation && validation.expression}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.order}</Label>
                        <Input
                            type="text"
                            value={order !== null ? order : ""}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10)
                                if (!Number.isNaN(parsed)) setOrder(parsed)
                                else setOrder(null)
                            }}
                        />
                        <Feedback>{validation && validation.order}</Feedback>
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
